/* ==========================================================================
   CottonTech theme — interactions (vanilla JS, no dependencies)
   Settings come from window.CT (layout/theme.liquid).
   Cart uses Shopify's Ajax cart API, so checkout, discounts and orders are Shopify's.
   ========================================================================== */
(() => {
  'use strict';

  const CT = window.CT || {};
  const routes = CT.routes || { root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change' };
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  /* ---------- helpers ---------- */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const icon = (name, cls = 'icon') => `<svg class="${cls}" aria-hidden="true" focusable="false"><use href="#i-${name}"></use></svg>`;
  const escapeHtml = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const safe = (name, fn) => { try { fn(); } catch (err) { console.error(`[CottonTech] ${name} failed:`, err); } };

  // cents -> "$39" / "$39.50" using the store's money format
  const money = (cents) => {
    const value = (Number(cents) || 0) / 100;
    const amount = Number.isInteger(value) ? value.toLocaleString('en-US') : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (CT.moneyFormat || '${{amount}}').replace(/\{\{\s*[\w]+\s*\}\}/, amount).replace(/<[^>]*>/g, '');
  };
  const dollars = (n) => Math.round((Number(n) || 0) * 100);
  const each2 = dollars(CT.bundle2Each || 35);
  const each3 = dollars(CT.bundle3Each || 33);
  const freeFrom = Number(CT.freeDeliveryFrom || 2);
  const deliveryFee = dollars(CT.deliveryFee || 4);
  const unitPrice = (count, base) => (count >= 3 ? each3 : count === 2 ? each2 : base);
  const priceFor = (count, base) => {
    const compare = count * base;
    const subtotal = count * unitPrice(count, base);
    return { compare, subtotal, savings: Math.max(0, compare - subtotal) };
  };
  // the real shirt colours, used when the theme setting is empty (CSS "navy" is a bright blue)
  const swatches = String(CT.swatches || '').split(/\n/).reduce((map, line) => {
    const [name, hex] = line.split(':').map((s) => (s || '').trim());
    if (name && hex) map[name.toLowerCase()] = hex;
    return map;
  }, { black: '#151515', navy: '#1f2b57', white: '#ffffff' });

  /* ---------- drawers & modal ---------- */
  let activePanel = null;
  let returnFocus = null;
  const focusables = (root) => $$('a[href], button:not([disabled]), input:not([type="hidden"]), textarea, select, [tabindex]:not([tabindex="-1"])', root)
    .filter((el) => el.offsetParent !== null);

  function openPanel(panel, trigger) {
    if (!panel) return;
    if (activePanel && activePanel !== panel) closePanel({ restoreFocus: false });
    returnFocus = trigger || document.activeElement;
    activePanel = panel;
    panel.removeAttribute('inert');
    panel.classList.add('is-open');
    if (panel.classList.contains('drawer')) $('[data-overlay]').classList.add('is-open');
    document.body.classList.add('is-locked');
    $$(`[aria-controls="${panel.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    window.setTimeout(() => {
      const target = $('button[data-close]', panel) || focusables(panel)[0];
      if (target) target.focus({ preventScroll: true });
    }, 80);
  }

  function closePanel({ restoreFocus = true } = {}) {
    const panel = activePanel;
    if (!panel) return;
    panel.classList.remove('is-open');
    panel.setAttribute('inert', '');
    $('[data-overlay]').classList.remove('is-open');
    document.body.classList.remove('is-locked');
    $$(`[aria-controls="${panel.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    activePanel = null;
    if (restoreFocus && returnFocus && document.contains(returnFocus)) returnFocus.focus({ preventScroll: true });
  }

  function initPanels() {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-open]');
      if (opener) {
        e.preventDefault();
        openPanel(document.getElementById(`${opener.dataset.open}-drawer`), opener);
        return;
      }
      const guide = e.target.closest('[data-open-size-guide], a[href="#size-guide"]');
      if (guide) {
        e.preventDefault();
        openPanel($('#size-guide'), guide);
        return;
      }
      if (e.target.closest('[data-close]') || e.target.closest('[data-overlay]')) closePanel();
    });

    document.addEventListener('keydown', (e) => {
      if (!activePanel) return;
      if (e.key === 'Escape') { closePanel(); return; }
      if (e.key !== 'Tab') return;
      const items = focusables(activePanel);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  /* ---------- header: hides on scroll down, returns on scroll up ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    let lastY = window.scrollY;
    let queued = false;
    window.addEventListener('scroll', () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY;
        if (!activePanel && Math.abs(delta) > 6) header.classList.toggle('is-hidden', delta > 0 && y > 320);
        lastY = y;
        queued = false;
      });
    }, { passive: true });
    header.addEventListener('focusin', () => header.classList.remove('is-hidden'));
  }

  /* ---------- cart (Shopify Ajax API) ---------- */
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const cartApi = {
    get: () => fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()),
    add: async (items) => {
      const r = await fetch(`${routes.cartAdd}.js`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ items }) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.description || data.message || 'This item could not be added to your cart.');
      return data;
    },
    change: (key, quantity) => fetch(`${routes.cartChange}.js`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ id: key, quantity }) }).then((r) => r.json())
  };

  function renderCart(cart, { bump = false } = {}) {
    if (!cart) return;
    const count = cart.item_count || 0;
    $$('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
      if (bump && count) { badge.classList.remove('is-bump'); void badge.offsetWidth; badge.classList.add('is-bump'); }
    });

    const drawer = $('#cart-drawer');
    if (!drawer) return;
    const empty = count === 0;
    $('[data-cart-heading-count]', drawer).textContent = empty ? '' : `(${count})`;
    $('[data-cart-empty]', drawer).hidden = !empty;
    $('[data-cart-items]', drawer).hidden = empty;
    $('[data-cart-foot]', drawer).hidden = empty;
    $('[data-cart-progress]', drawer).hidden = empty;
    if (empty) return;

    const base = Math.max(...cart.items.map((i) => i.original_price || 0));
    const need = freeFrom - count;
    let message;
    if (need > 0) message = `Add <strong>${need} more shirt${need > 1 ? 's' : ''}</strong> to unlock <strong>free delivery</strong> and <strong>${money(unitPrice(freeFrom, base))} each</strong>.`;
    else if (count < 3) message = `<strong>Free delivery unlocked.</strong> Add 1 more for <strong>${money(unitPrice(3, base))} each</strong>.`;
    else message = `<strong>Best price unlocked</strong> — ${money(unitPrice(count, base))} per shirt + free delivery.`;
    $('[data-cart-progress]', drawer).innerHTML =
      `<p>${message}</p><div class="progress" aria-hidden="true"><span style="width:${(Math.min(count, 3) / 3) * 100}%"></span></div>`;

    $('[data-cart-items]', drawer).innerHTML = cart.items.map((item) => {
      const img = item.image ? `${item.image}${item.image.includes('?') ? '&' : '?'}width=200` : '';
      return `
        <li class="cart-item" data-key="${escapeHtml(item.key)}">
          ${img ? `<img class="cart-item__img" src="${escapeHtml(img)}" alt="" width="84" height="105" loading="lazy">` : '<span class="cart-item__img"></span>'}
          <div>
            <p class="cart-item__title"><a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a></p>
            ${item.variant_title ? `<p class="cart-item__variant">${escapeHtml(item.variant_title)}</p>` : ''}
            <div class="cart-item__controls">
              <div class="qty" role="group" aria-label="Quantity, ${escapeHtml(item.variant_title || item.product_title)}">
                <button type="button" data-step="-1" aria-label="Remove one">${icon('minus', 'icon icon--xs')}</button>
                <output>${item.quantity}</output>
                <button type="button" data-step="1" aria-label="Add one">${icon('plus', 'icon icon--xs')}</button>
              </div>
              <button type="button" class="cart-item__remove" data-remove>Remove</button>
            </div>
          </div>
          <p class="cart-item__price">${money(item.original_line_price)}</p>
        </li>`;
    }).join('');

    const delivery = count >= freeFrom ? 0 : deliveryFee;
    $('[data-cart-subtotal]', drawer).textContent = money(cart.original_total_price);
    $('[data-cart-save-row]', drawer).hidden = !cart.total_discount;
    $('[data-cart-savings]', drawer).textContent = `−${money(cart.total_discount)}`;
    $('[data-cart-delivery]', drawer).textContent = delivery ? money(delivery) : 'Free';
    $('[data-cart-total]', drawer).textContent = money(cart.total_price + delivery);
  }

  function initCart() {
    renderCart(CT.cart);
    const drawer = $('#cart-drawer');
    if (!drawer) return;
    let busy = false;

    drawer.addEventListener('click', async (e) => {
      const item = e.target.closest('.cart-item');
      const step = e.target.closest('[data-step]');
      const remove = e.target.closest('[data-remove]');
      if (item && (step || remove) && !busy) {
        busy = true;
        const qty = Number($('output', item).textContent);
        const next = remove ? 0 : qty + Number(step.dataset.step);
        try {
          const cart = await cartApi.change(item.dataset.key, Math.max(0, next));
          renderCart(cart);
        } finally { busy = false; }
        const again = $(`.cart-item[data-key="${CSS.escape(item.dataset.key)}"] [data-step="${step ? step.dataset.step : 1}"]`, drawer);
        (again || $('[data-close]', drawer)).focus();
      }
      const checkout = e.target.closest('[data-checkout]');
      if (checkout) checkout.textContent = 'Opening checkout…';
    });

    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      const btn = $('[data-checkout]', drawer);
      if (btn) btn.textContent = 'Checkout';
      cartApi.get().then((cart) => renderCart(cart));
    });
  }

  /* ---------- product gallery (every colour's photos; choosing a colour jumps to its first photo) ---------- */
  function createGallery(root, product, colorIndex) {
    if (!root) return null;
    const track = $('[data-gallery-track]', root);
    const dots = $('[data-gallery-dots]', root);
    const prev = $('[data-gallery-prev]', root);
    const next = $('[data-gallery-next]', root);
    const slides = $$('.gallery__slide', track);
    let index = 0;
    let current;

    // which colour each photo belongs to: a colour's photos start at its variant image
    const colorOf = {};
    if (product && colorIndex > -1) {
      const startOf = {};
      product.variants.forEach((v) => { if (v.featured_media) startOf[v.featured_media.id] = v.options[colorIndex]; });
      let currentColor = null;
      (product.media || []).slice().sort((a, b) => a.position - b.position).forEach((m) => {
        if (startOf[m.id]) currentColor = startOf[m.id];
        colorOf[m.id] = /all colou?rs/i.test(m.alt || '') ? null : currentColor;
      });
    }
    const visible = () => slides;
    dots.innerHTML = slides.map((_, i) => `<button type="button" class="gallery__dot" aria-label="Show photo ${i + 1}"></button>`).join('');

    const update = () => {
      const vis = visible();
      $$('.gallery__dot', dots).forEach((d, i) => {
        d.classList.toggle('is-active', i === index);
        d.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      prev.disabled = index <= 0;
      next.disabled = index >= vis.length - 1;
      prev.hidden = next.hidden = vis.length < 2;
    };
    const go = (i, smooth = true) => {
      const vis = visible();
      index = Math.max(0, Math.min(vis.length - 1, i));
      track.scrollTo({ left: index * track.clientWidth, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
      update();
    };

    prev.addEventListener('click', () => go(index - 1));
    next.addEventListener('click', () => go(index + 1));
    dots.addEventListener('click', (e) => {
      const dot = e.target.closest('.gallery__dot');
      if (dot) go($$('.gallery__dot', dots).indexOf(dot));
    });
    let raf = 0;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        if (i !== index) { index = i; update(); }
      });
    }, { passive: true });
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); }
    });
    window.addEventListener('resize', () => go(index, false));

    return {
      show(color) {
        if (color === current) return;
        const first = current === undefined;
        current = color;
        const start = Math.max(0, slides.findIndex((s) => colorOf[s.dataset.mediaId] === color));
        go(start, !first);
      }
    };
  }

  /* ---------- buy box: bundle, colour & size picker ---------- */
  function initBuyBox(root) {
    const productEl = root.closest('[data-product]') || document;
    const jsonEl = $('[data-product-json]', productEl);
    if (!jsonEl) return;
    const product = JSON.parse(jsonEl.textContent);
    const optionNames = (product.options || []).map((o) => String(typeof o === 'string' ? o : o.name).toLowerCase());
    const ci = optionNames.findIndex((n) => n === 'color' || n === 'colour');
    const si = optionNames.findIndex((n) => n === 'size');
    const uniq = (i) => (i < 0 ? [null] : [...new Set(product.variants.map((v) => v.options[i]))]);
    const colors = uniq(ci);
    const sizes = uniq(si);
    const findVariant = (color, size) => product.variants.find((v) => (ci < 0 || v.options[ci] === color) && (si < 0 || v.options[si] === size));
    const isSoldOut = (color, size) => { const v = findVariant(color, size); return !v || !v.available; };
    const anyAvailable = product.variants.some((v) => v.available);
    const base = (product.variants.find((v) => v.available) || product.variants[0]).price;

    const gallery = createGallery($('[data-gallery]', productEl), product, ci);
    const priceEl = $('[data-price]', productEl);
    const isMainProduct = document.body.dataset.page === 'product';
    const params = new URLSearchParams(window.location.search);
    const startVariant = isMainProduct && product.variants.find((v) => String(v.id) === params.get('variant'));
    const startColor = startVariant && ci > -1 ? startVariant.options[ci] : colors[0];
    const bundleParam = isMainProduct ? Number(params.get('bundle')) : 0;
    const startQty = [1, 2, 3].includes(bundleParam) ? bundleParam : 1;
    const uid = Math.random().toString(36).slice(2, 8);
    const state = { qty: startQty, items: [{ color: startColor, size: null }] };

    const syncItems = () => {
      while (state.items.length < state.qty) {
        const prevItem = state.items[state.items.length - 1];
        state.items.push({ color: prevItem.color, size: prevItem.size });
      }
    };
    const activeItems = () => state.items.slice(0, state.qty);
    const bundles = [
      { qty: 1, title: 'Buy 1', note: 'Standard price' },
      { qty: 2, title: 'Buy 2', note: `${money(each2)} each · Free delivery`, tag: 'Most popular' },
      { qty: 3, title: 'Buy 3', note: `${money(each3)} each · Free delivery` }
    ];

    root.innerHTML = `
      <p class="opt-label" id="bundle-label-${uid}">Bundle &amp; save</p>
      <div class="bundles" role="radiogroup" aria-labelledby="bundle-label-${uid}">
        ${bundles.map((b) => {
          const p = priceFor(b.qty, base);
          return `
            <label class="bundle" data-bundle="${b.qty}">
              ${b.tag ? `<span class="bundle__tag">${b.tag}</span>` : ''}
              <input type="radio" name="bundle-${uid}" value="${b.qty}">
              <span class="bundle__radio" aria-hidden="true"></span>
              <span>
                <span class="bundle__title">${b.title}${p.savings ? `<span class="bundle__save">Save ${money(p.savings)}</span>` : ''}</span>
                <span class="bundle__note">${b.note}</span>
              </span>
              <span class="bundle__price">${money(p.subtotal)}${p.savings ? `<s>${money(p.compare)}</s>` : ''}</span>
            </label>`;
        }).join('')}
      </div>
      <div class="items" data-items></div>
      <div class="actions">
        <button type="button" class="btn btn--black btn--block btn--lg" data-add>Add to cart</button>
        <button type="button" class="btn btn--outline btn--block" data-buy-now>Buy it now</button>
        <p class="item-row__error" data-cart-error role="alert" hidden></p>
      </div>`;

    const itemsEl = $('[data-items]', root);
    const addBtn = $('[data-add]', root);
    const buyBtn = $('[data-buy-now]', root);
    const errorEl = $('[data-cart-error]', root);

    const swatchesHtml = (i) => (ci < 0 ? '' : `
      <div class="swatches" role="group" aria-label="Color, shirt ${i + 1}">
        ${colors.map((c) => `<button type="button" class="swatch" style="background:${escapeHtml(swatches[String(c).toLowerCase()] || String(c).toLowerCase())}" aria-label="${escapeHtml(c)}" title="${escapeHtml(c)}" data-color="${escapeHtml(c)}"></button>`).join('')}
      </div>`);
    const sizesHtml = (i) => (si < 0 ? '' : `
      <div class="sizes" role="group" aria-label="Size, shirt ${i + 1}">
        ${sizes.map((s) => `<button type="button" class="size" data-size="${escapeHtml(s)}">${escapeHtml(s)}</button>`).join('')}
      </div>`);
    const guideBtn = ''; // the size guide link sits above the description (product-main.liquid), not by the sizes

    const refreshRow = (row, it) => {
      $$('[data-color]', row).forEach((b) => {
        const on = b.dataset.color === it.color;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-pressed', on);
      });
      $$('[data-size]', row).forEach((b) => {
        const s = b.dataset.size;
        const out = isSoldOut(it.color, s);
        const on = it.size === s;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-pressed', on);
        b.classList.toggle('is-soldout', out);
        if (out) { b.setAttribute('aria-disabled', 'true'); b.title = 'Sold out'; b.setAttribute('aria-label', `${s}, sold out`); }
        else { b.removeAttribute('aria-disabled'); b.removeAttribute('title'); b.removeAttribute('aria-label'); }
      });
      const value = $('[data-value]', row);
      if (value) value.textContent = (it.color || '') + (state.qty > 1 && it.size ? ` · ${it.size}` : '');
      if (it.size) { row.classList.remove('has-error'); $('[data-error]', row).hidden = true; }
    };

    const renderItems = () => {
      const items = activeItems();
      if (si < 0) items.forEach((it) => { it.size = null; });
      if (state.qty === 1) {
        itemsEl.innerHTML = `
          <div class="item-row" data-item="0">
            ${ci < 0 ? '' : `<div class="option-group"><p class="opt-label"><span>Color</span><span class="value" data-value></span></p>${swatchesHtml(0)}</div>`}
            ${si < 0 ? '' : `<div class="option-group"><div class="opt-label"><span>Size</span>${guideBtn}</div>${sizesHtml(0)}</div>`}
            <p class="item-row__error" data-error hidden>Please select a size.</p>
          </div>`;
      } else {
        itemsEl.innerHTML = `<div class="opt-label"><span>Choose color &amp; size</span>${guideBtn}</div>` +
          items.map((it, i) => `
            <div class="item-row is-compact" data-item="${i}">
              <p class="opt-label"><span>Shirt ${i + 1}</span><span class="value" data-value></span></p>
              ${swatchesHtml(i)}
              ${sizesHtml(i)}
              <p class="item-row__error" data-error hidden>Please select a size for shirt ${i + 1}.</p>
            </div>`).join('');
      }
      $$('[data-item]', itemsEl).forEach((row) => refreshRow(row, state.items[Number(row.dataset.item)]));
    };

    const renderPrice = () => {
      const p = priceFor(state.qty, base);
      if (priceEl) {
        priceEl.innerHTML = `<span>${money(p.subtotal)}</span>` +
          (p.savings ? `<s>${money(p.compare)}</s><span class="save-tag">Save ${money(p.savings)}</span>` : '');
      }
      if (!anyAvailable) {
        addBtn.textContent = 'Sold out';
        addBtn.classList.add('is-muted');
        addBtn.disabled = true;
        buyBtn.hidden = true;
        return;
      }
      const ready = si < 0 || activeItems().every((it) => it.size);
      addBtn.classList.toggle('is-muted', !ready);
      addBtn.textContent = ready ? `Add to cart — ${money(p.subtotal)}` : (state.qty > 1 ? 'Select sizes' : 'Select a size');
    };

    const renderBundles = () => {
      $$('.bundle', root).forEach((label) => {
        const on = Number(label.dataset.bundle) === state.qty;
        label.classList.toggle('is-selected', on);
        $('input', label).checked = on;
      });
    };

    const renderAll = () => { syncItems(); renderBundles(); renderItems(); renderPrice(); };

    const validate = () => {
      const items = activeItems();
      let firstBad = null;
      items.forEach((it, i) => {
        const row = $(`[data-item="${i}"]`, itemsEl);
        const bad = si > -1 && !it.size;
        row.classList.toggle('has-error', bad);
        $('[data-error]', row).hidden = !bad;
        if (bad && !firstBad) firstBad = row;
      });
      if (!firstBad) {
        const lines = {};
        items.forEach((it) => {
          const v = findVariant(it.color, it.size);
          if (v) lines[v.id] = (lines[v.id] || 0) + 1;
        });
        return Object.entries(lines).map(([id, quantity]) => ({ id: Number(id), quantity }));
      }
      firstBad.classList.remove('shake');
      void firstBad.offsetWidth;
      firstBad.classList.add('shake');
      firstBad.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      const firstSize = $('.size:not(.is-soldout)', firstBad);
      if (firstSize) firstSize.focus({ preventScroll: true });
      return null;
    };

    const addToCart = async () => {
      const lines = validate();
      if (!lines) return false;
      errorEl.hidden = true;
      addBtn.disabled = buyBtn.disabled = true;
      try {
        await cartApi.add(lines);
        renderCart(await cartApi.get(), { bump: true });
        return true;
      } catch (err) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
        return false;
      } finally {
        addBtn.disabled = buyBtn.disabled = false;
        renderPrice();
      }
    };

    root.addEventListener('change', (e) => {
      if (e.target.name !== `bundle-${uid}`) return;
      state.qty = Number(e.target.value);
      renderAll();
    });

    root.addEventListener('click', async (e) => {
      const row = e.target.closest('[data-item]');
      const swatch = e.target.closest('[data-color]');
      const size = e.target.closest('[data-size]');
      if (row && (swatch || size)) {
        const it = state.items[Number(row.dataset.item)];
        if (swatch) {
          it.color = swatch.dataset.color;
          if (it.size && isSoldOut(it.color, it.size)) it.size = null;
          if (gallery) gallery.show(it.color);
        }
        if (size) {
          if (size.classList.contains('is-soldout')) return;
          it.size = size.dataset.size;
        }
        refreshRow(row, it);
        renderPrice();
        return;
      }
      if (e.target.closest('[data-add]')) {
        if (await addToCart()) openPanel($('#cart-drawer'), addBtn);
      }
      if (e.target.closest('[data-buy-now]')) {
        if (await addToCart()) window.location.href = `${routes.root}checkout`;
      }
    });

    // lets "shop by colour" cards on the product page switch the picker in place
    if (isMainProduct) {
      window.CT_selectProduct = (color, qty) => {
        if (qty && [1, 2, 3].includes(qty)) state.qty = qty;
        if (color && colors.includes(color)) {
          state.items[0].color = color;
          if (state.items[0].size && isSoldOut(color, state.items[0].size)) state.items[0].size = null;
          if (gallery) gallery.show(color);
        }
        renderAll();
      };
    }

    renderAll();
    if (gallery) gallery.show(startColor);
  }

  function initProductLinks() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('[data-select-color], [data-select-bundle]');
      if (!link || !window.CT_selectProduct) return;
      e.preventDefault();
      window.CT_selectProduct(link.dataset.selectColor, Number(link.dataset.selectBundle) || null);
      const target = $('[data-product]');
      if (target) target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    });
  }

  /* ---------- scroll reveal ---------- */
  let revealObserver = null;
  function initReveal(scope = document) {
    $$('.product-grid, .feature-list, .pillars, .stats', scope).forEach((group) => {
      $$('.reveal', group).forEach((el, i) => el.style.setProperty('--delay', `${i * 0.09}s`));
    });
    const els = $$('.reveal', scope);
    if (designMode || reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.12 });
    }
    els.forEach((el) => revealObserver.observe(el));
  }

  /* ---------- word-by-word heading fill & background parallax ---------- */
  let fills = [];
  let parallax = [];
  function prepareScrollEffects(scope = document) {
    $$('[data-fill-words]', scope).forEach((el) => {
      if (el.dataset.filled) return;
      el.dataset.filled = '1';
      const words = el.textContent.trim().split(/\s+/);
      el.innerHTML = words.map((w) => `<span class="fill-word">${escapeHtml(w)}</span>`).join(' ');
      if (reduceMotion || designMode) $$('.fill-word', el).forEach((w) => w.classList.add('is-filled'));
    });
    fills = $$('[data-fill-words]');
    parallax = reduceMotion ? [] : $$('[data-parallax]');
  }
  function initScrollEffects() {
    prepareScrollEffects();
    let queued = false;
    const update = () => {
      queued = false;
      const vh = window.innerHeight;
      if (!reduceMotion && !designMode) {
        fills.forEach((el) => {
          const top = el.getBoundingClientRect().top;
          const progress = Math.min(1, Math.max(0, (vh * 0.92 - top) / (vh * 0.42)));
          const words = el.children;
          const lit = Math.round(progress * words.length);
          for (let i = 0; i < words.length; i++) words[i].classList.toggle('is-filled', i < lit);
        });
      }
      parallax.forEach((el) => {
        const r = el.parentElement.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        const p = (r.top + r.height / 2 - vh / 2) / (vh + r.height);
        el.style.transform = `translate3d(0, ${(p * -16).toFixed(2)}%, 0)`;
      });
    };
    const onScroll = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  }

  /* ---------- stat counters ---------- */
  function initCounters(scope = document) {
    const els = $$('[data-count]', scope);
    if (!els.length || reduceMotion || designMode || !('IntersectionObserver' in window)) return;
    const run = (el) => {
      const target = Number(el.dataset.count);
      const suffix = el.dataset.suffix || '';
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / 1600);
        el.textContent = Math.round(target * (1 - Math.pow(1 - t, 3))) + suffix;
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        run(entry.target);
        io.unobserve(entry.target);
      });
    }, { threshold: 0.6 });
    els.forEach((el) => { el.textContent = `0${el.dataset.suffix || ''}`; io.observe(el); });
  }

  /* ---------- before / after comparison ---------- */
  function initCompare(scope = document) {
    $$('[data-compare]', scope).forEach((el) => {
      const range = $('.compare__range', el);
      const set = (v) => { el.style.setProperty('--pos', `${v}%`); range.value = v; };
      const fromPointer = (x) => {
        const r = el.getBoundingClientRect();
        set(Math.min(100, Math.max(0, ((x - r.left) / r.width) * 100)));
      };
      let dragging = false;
      el.addEventListener('pointerdown', (e) => { dragging = true; el.setPointerCapture(e.pointerId); fromPointer(e.clientX); });
      el.addEventListener('pointermove', (e) => { if (dragging) fromPointer(e.clientX); });
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((type) => el.addEventListener(type, () => { dragging = false; }));
      range.addEventListener('input', () => set(Number(range.value)));
      set(Number(range.value));
    });
  }

  /* ---------- theme editor: re-run when a section is edited ---------- */
  function initDesignMode() {
    if (!designMode) return;
    document.addEventListener('shopify:section:load', (e) => {
      const scope = e.target;
      safe('editor reveal', () => initReveal(scope));
      safe('editor fills', () => prepareScrollEffects(scope));
      safe('editor compare', () => initCompare(scope));
      safe('editor buy box', () => $$('[data-buybox]', scope).forEach(initBuyBox));
    });
  }

  /* ---------- boot ---------- */
  safe('reveal', () => initReveal());
  window.__ctReady = true;
  safe('panels', initPanels);
  safe('header', initHeader);
  safe('cart', initCart);
  safe('buy box', () => $$('[data-buybox]').forEach(initBuyBox));
  safe('product links', initProductLinks);
  safe('scroll effects', initScrollEffects);
  safe('counters', () => initCounters());
  safe('compare', () => initCompare());
  safe('design mode', initDesignMode);
})();
